import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Gift, Upload, Trash2, Download, Users, TrendingUp, Phone, Check, X, ChevronDown, ChevronUp, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCY_OPTIONS = [
  { value: 'solsticio', label: 'Por solsticios (4 al año)' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'anual', label: 'Anual' },
  { value: 'fecha_concreta', label: 'Fecha concreta' },
];

const SorteoConfigPanel = () => {
  const [config, setConfig] = useState(null);
  const [participants, setParticipants] = useState({ participants: [], total: 0, marketing_opt_in: 0, marketing_rate: 0, sorteos: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [selectedSorteo, setSelectedSorteo] = useState('');
  const [showLegal, setShowLegal] = useState(null); // 'bases' | 'privacy'
  const [showParticipants, setShowParticipants] = useState(true);

  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/sorteo/config`, { headers });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
        setForm(data);
      }
    } catch (e) { console.error(e); }
  }, []);

  const loadParticipants = useCallback(async (sorteoFilter) => {
    try {
      const url = sorteoFilter
        ? `${API_URL}/api/turbo-marketing/sorteo/participants?sorteo_name=${encodeURIComponent(sorteoFilter)}`
        : `${API_URL}/api/turbo-marketing/sorteo/participants`;
      const res = await fetch(url, { headers });
      if (res.ok) setParticipants(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    Promise.all([loadConfig(), loadParticipants()]).finally(() => setLoading(false));
  }, [loadConfig, loadParticipants]);

  const handleSaveConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/sorteo/config`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          is_active: form.is_active,
          title: form.title,
          subtitle: form.subtitle,
          description: form.description,
          frequency: form.frequency,
          custom_date: form.custom_date,
          bases_legales: form.bases_legales,
          politica_privacidad: form.politica_privacidad,
          marketing_checkbox_text: form.marketing_checkbox_text,
          marketing_sub_text: form.marketing_sub_text,
        }),
      });
      if (res.ok) {
        const updated = await res.json();
        setConfig(updated);
        setForm(updated);
        setEditing(false);
        toast.success('Configuración guardada');
      }
    } catch (e) { toast.error('Error al guardar'); }
  };

  const handleToggle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/sorteo/config`, {
        method: 'PUT', headers,
        body: JSON.stringify({ is_active: !config.is_active }),
      });
      if (res.ok) {
        const updated = await res.json();
        setConfig(updated);
        setForm(updated);
        toast.success(updated.is_active ? 'Sorteo activado' : 'Sorteo desactivado');
      }
    } catch (e) { toast.error('Error'); }
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API_URL}/api/turbo-marketing/sorteo/config/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(c => ({ ...c, image_url: data.image_url }));
        setForm(f => ({ ...f, image_url: data.image_url }));
        toast.success('Imagen subida');
      }
    } catch (e) { toast.error('Error al subir'); }
  };

  const handleExport = () => {
    const url = selectedSorteo
      ? `${API_URL}/api/turbo-marketing/sorteo/participants/export?sorteo_name=${encodeURIComponent(selectedSorteo)}`
      : `${API_URL}/api/turbo-marketing/sorteo/participants/export`;
    window.open(url, '_blank');
  };

  const handleFilterSorteo = (val) => {
    setSelectedSorteo(val);
    loadParticipants(val);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="sorteo-config-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Gift size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Sorteo</h3>
            <p className="text-sm text-gray-500">Captación de clientes y sorteos</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500">{config?.is_active ? 'Activo' : 'Inactivo'}</span>
          <Switch checked={config?.is_active || false} onCheckedChange={handleToggle} data-testid="sorteo-toggle" />
        </div>
      </div>

      {/* Config Card */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-gray-700">Configuración del sorteo</h4>
            {!editing ? (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-sorteo-config">
                Editar
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setEditing(false); setForm(config); }}>Cancelar</Button>
                <Button size="sm" onClick={handleSaveConfig} className="bg-purple-600 hover:bg-purple-700" data-testid="save-sorteo-config">Guardar</Button>
              </div>
            )}
          </div>

          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-600">Título</label>
                  <Input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="sorteo-title" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-600">Subtítulo</label>
                  <Input value={form.subtitle || ''} onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))} data-testid="sorteo-subtitle" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-600">Descripción</label>
                <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full min-h-[60px] rounded-lg border p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500" data-testid="sorteo-description" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-600">Frecuencia</label>
                  <select value={form.frequency || 'solsticio'} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                    className="w-full rounded-lg border p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20" data-testid="sorteo-frequency">
                    {FREQUENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {form.frequency === 'fecha_concreta' && (
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-600">Fecha del sorteo</label>
                    <Input type="date" value={form.custom_date || ''} onChange={e => setForm(f => ({ ...f, custom_date: e.target.value }))} />
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-600">Texto checkbox marketing</label>
                <Input value={form.marketing_checkbox_text || ''} onChange={e => setForm(f => ({ ...f, marketing_checkbox_text: e.target.value }))} data-testid="sorteo-mkt-text" />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-600">Subtexto marketing</label>
                <Input value={form.marketing_sub_text || ''} onChange={e => setForm(f => ({ ...f, marketing_sub_text: e.target.value }))} />
              </div>

              {/* Legal texts */}
              <div className="space-y-3 pt-2 border-t">
                <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><FileText size={16} /> Textos legales</h5>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-600">Bases legales del sorteo</label>
                  <textarea value={form.bases_legales || ''} onChange={e => setForm(f => ({ ...f, bases_legales: e.target.value }))}
                    className="w-full min-h-[100px] rounded-lg border p-3 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    placeholder="Pega aquí las bases legales del sorteo..." data-testid="sorteo-bases" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-600">Política de privacidad</label>
                  <textarea value={form.politica_privacidad || ''} onChange={e => setForm(f => ({ ...f, politica_privacidad: e.target.value }))}
                    className="w-full min-h-[100px] rounded-lg border p-3 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    placeholder="Pega aquí la política de privacidad..." data-testid="sorteo-privacy" />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Título:</span><span className="font-medium">{config?.title}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Subtítulo:</span><span className="font-medium text-right max-w-[60%]">{config?.subtitle}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Frecuencia:</span><span className="font-medium">{FREQUENCY_OPTIONS.find(o => o.value === config?.frequency)?.label || config?.frequency}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Bases legales:</span><span className="font-medium">{config?.bases_legales ? 'Configuradas' : 'Sin configurar'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Política privacidad:</span><span className="font-medium">{config?.politica_privacidad ? 'Configurada' : 'Sin configurar'}</span></div>
            </div>
          )}

          {/* Image */}
          <div className="pt-3 border-t">
            <label className="text-sm font-medium text-gray-600 mb-2 block">Imagen del banner</label>
            {config?.image_url ? (
              <div className="relative rounded-xl overflow-hidden h-36 group">
                <img src={config.image_url.startsWith('/api') ? `${API_URL}${config.image_url}` : config.image_url}
                  alt="" className="w-full h-full object-cover" />
                <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                  <Upload size={24} className="text-white" />
                  <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleImageUpload(e.target.files[0])} />
                </label>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-28 rounded-xl border-2 border-dashed border-gray-200 cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-colors">
                <Upload size={24} className="text-gray-300 mb-1" />
                <span className="text-sm text-gray-400">Subir imagen</span>
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleImageUpload(e.target.files[0])} />
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Participants */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setShowParticipants(!showParticipants)} className="flex items-center gap-2">
              <Users size={18} className="text-purple-600" />
              <h4 className="font-medium text-gray-700">Participantes</h4>
              {showParticipants ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </button>
            <div className="flex items-center gap-2">
              {participants.sorteos?.length > 0 && (
                <select value={selectedSorteo} onChange={e => handleFilterSorteo(e.target.value)}
                  className="text-sm border rounded-lg px-2 py-1.5" data-testid="sorteo-filter">
                  <option value="">Todos los sorteos</option>
                  {participants.sorteos.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5" data-testid="export-participants">
                <Download size={14} /> CSV
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-xl bg-purple-50 text-center">
              <p className="text-2xl font-bold text-purple-700">{participants.total}</p>
              <p className="text-xs text-purple-500">Total</p>
            </div>
            <div className="p-3 rounded-xl bg-green-50 text-center">
              <p className="text-2xl font-bold text-green-700">{participants.marketing_opt_in}</p>
              <p className="text-xs text-green-500">Marketing</p>
            </div>
            <div className="p-3 rounded-xl bg-amber-50 text-center">
              <p className="text-2xl font-bold text-amber-700">{participants.marketing_rate}%</p>
              <p className="text-xs text-amber-500">Opt-in rate</p>
            </div>
          </div>

          {showParticipants && (
            participants.participants.length === 0 ? (
              <p className="text-center text-gray-400 py-6 text-sm">Sin participantes todavía</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {participants.participants.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 text-sm">
                    <div className="flex items-center gap-3">
                      <Phone size={14} className="text-gray-400" />
                      <span className="font-mono font-medium">{p.phone}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {p.accept_marketing ? (
                        <span className="flex items-center gap-1 text-green-600 text-xs"><Check size={12} /> Marketing</span>
                      ) : (
                        <span className="flex items-center gap-1 text-gray-400 text-xs"><X size={12} /> Marketing</span>
                      )}
                      <span className="text-xs text-gray-400">{new Date(p.created_at).toLocaleDateString('es')}</span>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SorteoConfigPanel;
